// MedicineTemplateTableRow.tsx
import React, { useEffect, useState } from "react";
import {
  FormControl,
  Grid,
  MenuItem,
  Select,
  TableCell,
  TableRow,
} from "@material-ui/core";

import FrequencyPerWeekAutocomplete from "./FrequencyPerWeekAutocomplete";
import MedicineAutocomplete from "./MedicineAutocomplete";
import {
  instructionDropdownOptions,
  medicineMeasurements,
  medicineType,
  parseBeforeFoodDropdownValueFromMedDb,
} from "./stateUtils";
import { Delete } from "@material-ui/icons";

/**
 * Types
 */
export type Medicine = {
  drugName?: string;
  drugType?: string;
  strength?: string | number;
  strengthUnit?: string;
  instruction?: string;
  frequencyPerWeek?: string;
  freqMorn?: string | number;
  freqAfter?: string | number;
  freqEvening?: string | number;
  freqNight?: string | number;
  duration?: string | number;
  durationUnit?: string;
  beforeFood?: string | number;
  [k: string]: any;
};

type OnChangeHandler = (medicine: Medicine, key?: string, value?: any) => void;

type Props = {
  medicine: Medicine;
  onChangeHandler: OnChangeHandler;
  onDeleteHandler?: () => void;
};

const parseFreq = (value: any): number => {
  return isNaN(Number(value)) || value === "" ? 0 : Number(value);
};

/**
 * Component
 */
const MedicineTableRow: React.FC<Props> = ({
  medicine: medicine,
  onChangeHandler,
  onDeleteHandler,
}) => {
  // Local controlled inputs for daily frequency UI
  const [freqMorn, setFreqMorn] = useState<string | number>(
    medicine.freqMorn ?? 0
  );
  const [freqAfter, setFreqAfter] = useState<string | number>(
    medicine.freqAfter ?? 0
  );
  const [freqEvening, setFreqEvening] = useState<string | number>(
    medicine.freqEvening ?? 0
  );
  const [freqNight, setFreqNight] = useState<string | number>(
    medicine.freqNight ?? 0
  );

  // Keep local inputs in sync when props change
  useEffect(() => {
    setFreqMorn(medicine.freqMorn ?? 0);
    setFreqAfter(medicine.freqAfter ?? 0);
    setFreqEvening(medicine.freqEvening ?? 0);
    setFreqNight(medicine.freqNight ?? 0);
  }, [
    medicine.freqMorn,
    medicine.freqAfter,
    medicine.freqEvening,
    medicine.freqNight,
  ]);

  const onMedicineSuggestionClickHandler = (newValue: any) => {
    if (newValue && newValue !== "") {
      onChangeHandler(
        {
          ...medicine,
          drugType: newValue.type,
          strengthUnit: newValue.unit,
          strength: newValue.strength,
          instruction: newValue.instruction,
          frequencyPerWeek:
            newValue.frequencyPerWeek === undefined
              ? "Everyday"
              : newValue.frequencyPerWeek,
          freqMorn:
            !newValue.morning || isNaN(newValue.morning)
              ? 0
              : parseFreq(newValue.morning),
          freqAfter:
            !newValue.afternoon || isNaN(newValue.afternoon)
              ? 0
              : parseFreq(newValue.afternoon),
          freqEvening:
            !newValue.evening || isNaN(newValue.evening)
              ? 0
              : parseFreq(newValue.evening),
          freqNight:
            !newValue.night || isNaN(newValue.night)
              ? 0
              : parseFreq(newValue.night),
          beforeFood: parseBeforeFoodDropdownValueFromMedDb(
            newValue.beforeFood
          ),
        },
        "drugName",
        newValue.name
      );
    }
  };

  const updateMedicineName = (newValue: string) => {
    onChangeHandler(medicine, "drugName", newValue);
  };

  const MedicineTypeSelect: React.FC = () => (
    <Select
      value={medicine.drugType ?? ""}
      onChange={(e) => onChangeHandler(medicine, "drugType", e.target.value)}
      disableUnderline
      style={{
        color: "var(--darkbluecolorone)",
        marginTop: 10,
        marginLeft: 5,
        fontSize: 14,
      }}
    >
      {medicineType.map((item: string, index: number) => (
        <MenuItem value={item} key={index}>
          {item}
        </MenuItem>
      ))}
    </Select>
  );

  const MedicineDurationInput = () => (
    <>
      <input
        type={medicine.durationUnit === "null" ? "text" : "number"}
        value={
          medicine.durationUnit === "null"
            ? "-"
            : String(medicine.duration ?? "")
        }
        disabled={medicine.durationUnit === "null"}
        onChange={(e) => {
          const val = e.target.value;
          const regex = /^\d+(\.\d+)?$/;
          if (regex.test(val) || val === "") {
            onChangeHandler(medicine, "duration", val);
          } else {
            // ignore invalid input
            e.preventDefault();
          }
        }}
        style={{
          display: "flex",
          alignItems: "center",
          width: 60,
          padding: 5,
          border: "1px solid #00000026",
          height: 40,
          borderRadius: 5,
          fontFamily: "Fira Sans",
          fontSize: 14,
          color: "#000000cc",
        }}
      />

      <Select
        value={medicine.durationUnit ?? "null"}
        onChange={(e) =>
          onChangeHandler(medicine, "durationUnit", e.target.value)
        }
        disableUnderline
        style={{
          display: "block",
          width: "fit-content",
          color: "var(--darkbluecolorone)",
          marginTop: 10,
          marginLeft: 5,
          fontSize: 14,
        }}
      >
        <MenuItem value="null">Null</MenuItem>
        <MenuItem value="day">Day(s)</MenuItem>
        <MenuItem value="week">Week(s)</MenuItem>
        <MenuItem value="month">Month(s)</MenuItem>
      </Select>
    </>
  );

  const MedicineDosageInput = () => (
    <>
      <input
        type="text"
        className="field_input form-control"
        style={{
          width: 80,
          marginRight: 10,
          fontSize: 14,
          fontWeight: "normal",
        }}
        value={
          medicine.strengthUnit === "NA" ? "-" : String(medicine.strength ?? "")
        }
        disabled={medicine.strengthUnit === "NA"}
        onChange={(e) => {
          const regex = /^[0-9.\/]*$/;
          if (regex.test(e.target.value) || e.target.value === "") {
            onChangeHandler(medicine, "strength", e.target.value);
          }
        }}
      />

      <Select
        id={`med-strength-unit-${medicine.id}`}
        value={medicine.strengthUnit ?? ""}
        onChange={(e) =>
          onChangeHandler(medicine, "strengthUnit", e.target.value)
        }
        disableUnderline
        style={{
          display: "block",
          width: "fit-content",
          color: "var(--darkbluecolorone)",
          marginTop: 10,
          marginLeft: 5,
          fontSize: 14,
        }}
      >
        {medicineMeasurements.map((item: string, index: number) => (
          <MenuItem value={item} key={index}>
            {item}
          </MenuItem>
        ))}
      </Select>
    </>
  );

  const MedicineDailyFrequencyInput: React.FC = () => {
    // handlers for blur / increment / decrement
    const onBlurFreq = (value: string | number, key: string) => {
      const v = String(value);
      const regex = /^([1-9](\.\d)?|0(\.\d)?)$/;
      if (regex.test(v)) {
        onChangeHandler(medicine, key, v);
      } else {
        if (v.length > 0) {
          onChangeHandler(medicine, key, v[0]);
        } else {
          onChangeHandler(medicine, key, 0);
        }
      }
    };

    const incDec = (key: string, currentValue: any, delta: number) => {
      const cur = parseFloat(String(currentValue || 0)) || 0;
      const newVal = Math.max(
        0,
        Math.min(9, Math.round((cur + delta) * 2) / 2)
      );
      onChangeHandler(medicine, key, newVal);
    };

    return (
      <>
        {/* Morning */}
        <span>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              width: 60,
              padding: 2,
              border: "1px solid #00000026",
              height: 40,
              borderRadius: 5,
              fontFamily: "Fira Sans",
              fontSize: 14,
              color: "#000000cc",
            }}
          >
            <input
              type="text"
              value={String(freqMorn)}
              onBlur={() => onBlurFreq(freqMorn, "freqMorn")}
              onChange={(e) => {
                const regex = /^\d?\.\d?$|^\d$/;
                if (regex.test(e.target.value) || e.target.value === "")
                  setFreqMorn(e.target.value);
              }}
              style={{
                fontFamily: "Fira Sans",
                fontSize: 14,
                height: "100%",
                paddingLeft: 5,
                width: "90%",
                border: "none",
                color: "#000000cc",
              }}
            />
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              <button
                tabIndex={-1}
                style={{
                  padding: 0,
                  border: "none",
                  background: "none",
                  fontSize: 10,
                }}
                onClick={() => incDec("freqMorn", medicine.freqMorn, 0.5)}
              >
                ▲
              </button>
              <button
                tabIndex={-1}
                style={{
                  padding: 0,
                  border: "none",
                  background: "none",
                  fontSize: 10,
                }}
                onClick={() => incDec("freqMorn", medicine.freqMorn, -0.5)}
              >
                ▼
              </button>
            </div>
          </div>
        </span>
        &nbsp; - &nbsp;
        {/* Afternoon */}
        <span>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              width: 60,
              padding: 2,
              border: "1px solid #00000026",
              height: 40,
              borderRadius: 5,
              fontFamily: "Fira Sans",
              fontSize: 14,
              color: "#000000cc",
            }}
          >
            <input
              type="text"
              value={String(freqAfter)}
              onBlur={() => onBlurFreq(freqAfter, "freqAfter")}
              onChange={(e) => {
                const regex = /^\d?\.\d?$|^\d$/;
                if (regex.test(e.target.value) || e.target.value === "")
                  setFreqAfter(e.target.value);
              }}
              style={{
                fontFamily: "Fira Sans",
                fontSize: 14,
                height: "100%",
                paddingLeft: 5,
                width: "90%",
                border: "none",
                color: "#000000cc",
              }}
            />
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              <button
                tabIndex={-1}
                style={{
                  padding: 0,
                  border: "none",
                  background: "none",
                  fontSize: 10,
                }}
                onClick={() => incDec("freqAfter", medicine.freqAfter, 0.5)}
              >
                ▲
              </button>
              <button
                tabIndex={-1}
                style={{
                  padding: 0,
                  border: "none",
                  background: "none",
                  fontSize: 10,
                }}
                onClick={() => incDec("freqAfter", medicine.freqAfter, -0.5)}
              >
                ▼
              </button>
            </div>
          </div>
        </span>
        &nbsp; - &nbsp;
        {/* Evening */}
        <span>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              width: 60,
              padding: 2,
              border: "1px solid #00000026",
              height: 40,
              borderRadius: 5,
              fontFamily: "Fira Sans",
              fontSize: 14,
              color: "#000000cc",
            }}
          >
            <input
              type="text"
              value={String(freqEvening)}
              onBlur={() => onBlurFreq(freqEvening, "freqEvening")}
              onChange={(e) => {
                const regex = /^\d?\.\d?$|^\d$/;
                if (regex.test(e.target.value) || e.target.value === "")
                  setFreqEvening(e.target.value);
              }}
              style={{
                fontFamily: "Fira Sans",
                fontSize: 14,
                height: "100%",
                paddingLeft: 5,
                width: "90%",
                border: "none",
                color: "#000000cc",
              }}
            />
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              <button
                tabIndex={-1}
                style={{
                  padding: 0,
                  border: "none",
                  background: "none",
                  fontSize: 10,
                }}
                onClick={() => incDec("freqEvening", medicine.freqEvening, 0.5)}
              >
                ▲
              </button>
              <button
                tabIndex={-1}
                style={{
                  padding: 0,
                  border: "none",
                  background: "none",
                  fontSize: 10,
                }}
                onClick={() =>
                  incDec("freqEvening", medicine.freqEvening, -0.5)
                }
              >
                ▼
              </button>
            </div>
          </div>
        </span>
        &nbsp; - &nbsp;
        {/* Night */}
        <span>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              width: 60,
              padding: 2,
              border: "1px solid #00000026",
              height: 40,
              borderRadius: 5,
              fontFamily: "Fira Sans",
              fontSize: 14,
              color: "#000000cc",
            }}
          >
            <input
              type="text"
              value={String(freqNight)}
              onBlur={() => onBlurFreq(freqNight, "freqNight")}
              onChange={(e) => {
                const regex = /^\d?\.\d?$|^\d$/;
                if (regex.test(e.target.value) || e.target.value === "")
                  setFreqNight(e.target.value);
              }}
              style={{
                fontFamily: "Fira Sans",
                fontSize: 14,
                height: "100%",
                paddingLeft: 5,
                width: "90%",
                border: "none",
                color: "#000000cc",
              }}
            />
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              <button
                tabIndex={-1}
                style={{
                  padding: 0,
                  border: "none",
                  background: "none",
                  fontSize: 10,
                }}
                onClick={() => incDec("freqNight", medicine.freqNight, 0.5)}
              >
                ▲
              </button>
              <button
                tabIndex={-1}
                style={{
                  padding: 0,
                  border: "none",
                  background: "none",
                  fontSize: 10,
                }}
                onClick={() => incDec("freqNight", medicine.freqNight, -0.5)}
              >
                ▼
              </button>
            </div>
          </div>
        </span>
      </>
    );
  };

  const MedicineWeeklyFrequencySelect = () => {
    return (
      <FrequencyPerWeekAutocomplete
        value={medicine.frequencyPerWeek}
        onSelectHandler={(newValue: any) =>
          onChangeHandler(medicine, "frequencyPerWeek", newValue)
        }
        onTextChangeHandler={(newValue: any) =>
          onChangeHandler(medicine, "frequencyPerWeek", newValue)
        }
      />
    );
  };

  const InstructionInput = () => (
    <Grid container spacing={0}>
      <Grid item xs={11}>
        <input
          type="text"
          className="field_input form-control"
          id={`med-instruction-${medicine.id}`}
          style={{ width: "100%", fontSize: 14, fontWeight: "normal" }}
          placeholder="Enter instructions"
          value={medicine.instruction ?? ""}
          onChange={(e) =>
            onChangeHandler(medicine, "instruction", e.target.value)
          }
        />
        <div style={{ width: 150 }}>
          <Select
            id={`instruction-dropdown-${medicine.id}`}
            style={{
              color: "var(--darkbluecolorone)",
              marginTop: 10,
              marginLeft: 5,
              fontSize: 14,
            }}
            value={medicine.beforeFood ?? ""}
            onChange={(e) =>
              onChangeHandler(medicine, "beforeFood", e.target.value)
            }
            disableUnderline
            displayEmpty
          >
            {instructionDropdownOptions.map(
              (
                item: (typeof instructionDropdownOptions)[number],
                index: number
              ) => (
                <MenuItem value={item.value} key={index}>
                  {item.text}
                </MenuItem>
              )
            )}
          </Select>
        </div>
      </Grid>

      {onDeleteHandler && (
        <Grid item xs={1}>
          <span className="icon">
            <Delete
              style={{
                marginTop: "0.5em",
                marginLeft: "1em",
                cursor: "pointer",
              }}
              onClick={() => onDeleteHandler()}
            />
          </span>
        </Grid>
      )}
    </Grid>
  );

  if (!medicine) return null;

  return (
    <TableRow key={medicine.id}>
      <TableCell
        align="left"
        style={{ borderBottom: "none", verticalAlign: "center" }}
      >
        <MedicineAutocomplete
          searchTemplates={true}
          medicineName={medicine.drugName}
          onSelectHandler={onMedicineSuggestionClickHandler}
          onNameChangeHandler={updateMedicineName}
        />
        <MedicineTypeSelect />
      </TableCell>

      <TableCell
        align="left"
        style={{ borderBottom: "none", verticalAlign: "top" }}
      >
        <MedicineDosageInput />
      </TableCell>

      <TableCell
        align="left"
        style={{ borderBottom: "none", display: "flex", alignItems: "center" }}
      >
        <MedicineDailyFrequencyInput />
      </TableCell>

      <TableCell
        align="left"
        style={{ borderBottom: "none", verticalAlign: "top" }}
      >
        <MedicineWeeklyFrequencySelect />
      </TableCell>

      <TableCell style={{ borderBottom: "none", verticalAlign: "top" }}>
        <MedicineDurationInput />
      </TableCell>

      <TableCell
        align="left"
        style={{ borderBottom: "none", verticalAlign: "top" }}
      >
        <InstructionInput />
      </TableCell>
    </TableRow>
  );
};

export default MedicineTableRow;
